import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import { apiService } from "../services/api";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "click"];
const ACTIVITY_THROTTLE_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;

export function useInactivityLogout() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const touchActivity = useAuthStore((state) => state.touchActivity);
  const logout = useAuthStore((state) => state.logout);
  const isSessionInactive = useAuthStore((state) => state.isSessionInactive);
  const lastThrottledTouchRef = useRef(0);

  const expireSession = () => {
    logout(true);
    apiService.logout().catch(() => {});
  };

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    if (isSessionInactive()) {
      expireSession();
      return undefined;
    }

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastThrottledTouchRef.current < ACTIVITY_THROTTLE_MS) return;
      lastThrottledTouchRef.current = now;
      touchActivity();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (isSessionInactive()) {
        expireSession();
        return;
      }
      recordActivity();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(() => {
      if (isSessionInactive()) {
        expireSession();
      }
    }, CHECK_INTERVAL_MS);

    recordActivity();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, touchActivity, logout, isSessionInactive]);
}

export default useInactivityLogout;
